import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

interface AlgoTradeChartProps {
  priceHistory: Array<{ t: string; p: number }>;
  entryPrice: number;
  stopPrice?: number;
  targetPrice?: number;
  symbol: string;
}

// All chart times rendered in ET — the price history covers market hours
// and the "all times Eastern" rule applies. Without timeZone, formatters
// fall back to browser-local which drifts labels by a full hour for users
// outside US/Eastern.
const ET = 'America/New_York';

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString('en-US', { timeZone: ET, month: 'short', day: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' });
  } catch {
    return ts;
  }
}

function formatXTick(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    // Include hour:minute so multiple ticks on the same calendar day don't
    // collapse to identical labels (same root cause as the stock and options
    // chart fixes).
    const date = d.toLocaleDateString('en-US', { timeZone: ET, month: 'numeric', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit', hour12: true });
    return `${date} ${time}`;
  } catch {
    return '';
  }
}

class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '8px 0', fontSize: 12, color: '#64748b' }}>
          Chart unavailable
        </div>
      );
    }
    return this.props.children;
  }
}

const AlgoTradeChartInner: React.FC<AlgoTradeChartProps> = ({
  priceHistory,
  entryPrice,
  stopPrice,
  targetPrice,
  symbol,
}) => {
  if (!priceHistory || priceHistory.length < 2) return null;

  const gradientId = `algoTradeGrad-${symbol}`;

  const data = priceHistory.map(pt => ({
    ts: pt.t,
    close: typeof pt.p === 'number' && isFinite(pt.p) ? pt.p : 0,
  }));

  const closes = data.map(d => d.close).filter(c => c > 0);
  if (closes.length === 0) return null;

  const refPrices = [entryPrice];
  if (stopPrice) refPrices.push(stopPrice);
  if (targetPrice) refPrices.push(targetPrice);

  const minClose = Math.min(...closes, ...refPrices);
  const maxClose = Math.max(...closes, ...refPrices);
  const range = maxClose - minClose;
  const padding = range > 0 ? range * 0.08 : maxClose * 0.02;
  const yMin = Math.floor((minClose - padding) * 100) / 100;
  const yMax = Math.ceil((maxClose + padding) * 100) / 100;

  const tickIndices: number[] = [];
  const step = Math.max(1, Math.floor(data.length / 6));
  for (let i = 0; i < data.length; i += step) tickIndices.push(i);
  const ticks = tickIndices.map(i => data[i].ts);

  const lastClose = closes[closes.length - 1];
  const isProfit = lastClose >= entryPrice;
  const areaColor = isProfit ? '#22c55e' : '#ef4444';

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 8 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>Price History</div>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={areaColor} stopOpacity={0.3} />
              <stop offset="100%" stopColor={areaColor} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="ts"
            ticks={ticks}
            tickFormatter={formatXTick}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
            tickLine={false}
          />
          <YAxis
            domain={[yMin, yMax]}
            tick={{ fill: '#64748b', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v: number) => `$${v.toFixed(0)}`}
            width={50}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#1e293b',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '6px',
              fontSize: '0.85em',
            }}
            labelFormatter={formatTimestamp}
            formatter={(value: number | undefined) => [`$${(value ?? 0).toFixed(2)}`, 'Price']}
          />
          <ReferenceLine
            y={entryPrice}
            stroke="#3b82f6"
            strokeDasharray="4 4"
            label={{ value: 'Entry', fill: '#3b82f6', fontSize: 10, position: 'left' }}
          />
          {targetPrice && (
            <ReferenceLine
              y={targetPrice}
              stroke="#22c55e"
              strokeDasharray="4 4"
              label={{ value: 'Target', fill: '#22c55e', fontSize: 10, position: 'left' }}
            />
          )}
          {stopPrice && (
            <ReferenceLine
              y={stopPrice}
              stroke="#ef4444"
              strokeDasharray="4 4"
              label={{ value: 'Stop', fill: '#ef4444', fontSize: 10, position: 'left' }}
            />
          )}
          <Area
            type="monotone"
            dataKey="close"
            stroke={areaColor}
            strokeWidth={1.5}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{ r: 3, fill: areaColor }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const AlgoTradeChart: React.FC<AlgoTradeChartProps> = (props) => (
  <ChartErrorBoundary>
    <AlgoTradeChartInner {...props} />
  </ChartErrorBoundary>
);
